import { useCallback, useEffect, useMemo, useState } from 'react';
import { apiFetch } from '../../lib/api-client.js';
import { supabase } from '../../lib/supabase-client.js';

interface RosterDriver {
  id: string | number;
  first_name?: string;
  last_name?: string;
  driver_id?: string;
  status?: string;
  role?: string;
  assigned_terminal_id?: string | number;
  assigned_terminal_name?: string | null;
  terminal_name?: string;
  staffing_type?: string;
}

interface Terminal {
  id: string | number;
  name?: string;
  code?: string;
  fedex_code?: string;
  terminal_name?: string;
  label?: string;
}

interface AttendanceRecord {
  id: string | number;
  employee_id: string | number;
  date: string;
  present: boolean;
  entry_type: string;
  absence_reason: string | null;
  deliveries: number | null;
  pickups: number | null;
  total_stops: number | null;
  miles: number | null;
  truck_number: string | null;
  service_score: number | null;
  jumping_for_employee_id: string | number | null;
  notes: string | null;
  work_terminal_id: string | number | null;
  route_type: string | null;
  route_id: string | null;
  had_jumper: boolean | null;
  status?: string;
}

interface ClockInRecord {
  employee_id: string | number;
  clock_in_time?: string;
  clock_out_time?: string;
  clock_in_timestamp?: string;
  clock_out_timestamp?: string;
}

interface DirectoryResponse {
  error?: string;
  employees?: RosterDriver[];
  terminals?: Terminal[];
}

const ATTENDANCE_SELECT = 'id, employee_id, date, present, entry_type, absence_reason, deliveries, pickups, total_stops, miles, truck_number, service_score, jumping_for_employee_id, notes, work_terminal_id, route_type, route_id, had_jumper';
const CLOCK_INS_SELECT = '*';

export function useDailyEntryRoster(selectedDate: string, selectedTerminalId: string) {
  const [drivers, setDrivers] = useState<RosterDriver[]>([]);
  const [terminals, setTerminals] = useState<Terminal[]>([]);
  const [attendance, setAttendance] = useState<AttendanceRecord[]>([]);
  const [clockIns, setClockIns] = useState<ClockInRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const [directoryData, attendanceRes, clockInsRes] = await Promise.all([
        apiFetch<DirectoryResponse>('/api/directory?status=active'),
        supabase.from('attendance').select(ATTENDANCE_SELECT).eq('date', selectedDate),
        supabase.from('clock_ins').select(CLOCK_INS_SELECT).eq('date', selectedDate),
      ]);

      if (directoryData.error) throw new Error(directoryData.error);
      if (attendanceRes.error) throw attendanceRes.error;
      if (clockInsRes.error) throw clockInsRes.error;

      const directoryEmployees = directoryData.employees || [];
      const directoryTerminals = directoryData.terminals || [];

      setDrivers(
        directoryEmployees.filter(
          (employee: RosterDriver) => employee.status === 'active' && (employee.role === 'driver' || employee.role === 'lead_driver'),
        ),
      );
      setTerminals(directoryTerminals);
      setAttendance(attendanceRes.data || []);
      setClockIns(clockInsRes.data || []);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Daily Entry roster load failed');
      setDrivers([]);
      setTerminals([]);
      setAttendance([]);
      setClockIns([]);
    } finally {
      setLoading(false);
    }
  }, [selectedDate]);

  useEffect(() => {
    load();
  }, [load]);

  const terminalsById = useMemo(() => Object.fromEntries(terminals.map((terminal) => [String(terminal.id), terminal])), [terminals]);
  const enteredIds = useMemo(() => new Set(attendance.map((item) => item.employee_id)), [attendance]);
  const attendanceByEmployee = useMemo(() => Object.fromEntries(attendance.map((item) => [item.employee_id, item])), [attendance]);
  const clockByEmployee = useMemo(() => Object.fromEntries(clockIns.map((item) => [item.employee_id, item])), [clockIns]);

  const enrichedDrivers = useMemo(() => {
    return drivers.map((driver) => {
      const terminal = terminalsById[String(driver.assigned_terminal_id)] || null;
      return {
        ...driver,
        assigned_terminal_name: terminal?.name || driver.assigned_terminal_name || null,
      };
    });
  }, [drivers, terminalsById]);

  const filteredRoster = useMemo(() => {
    return enrichedDrivers.filter((driver) => {
      if (selectedTerminalId && selectedTerminalId !== 'all' && String(driver.assigned_terminal_id) !== String(selectedTerminalId)) return false;
      if (search) {
        const query = search.toLowerCase();
        const name = `${driver.first_name || ''} ${driver.last_name || ''}`.toLowerCase();
        const id = (driver.driver_id || '').toLowerCase();
        if (!name.includes(query) && !id.includes(query)) return false;
      }
      if (statusFilter === 'entered' && !enteredIds.has(driver.id)) return false;
      if (statusFilter === 'remaining' && enteredIds.has(driver.id)) return false;
      return true;
    });
  }, [enrichedDrivers, enteredIds, search, selectedTerminalId, statusFilter]);

  const sortedRoster = useMemo(() => {
    return [...filteredRoster].sort((a, b) => {
      const aEntry = attendanceByEmployee[a.id];
      const bEntry = attendanceByEmployee[b.id];
      const aClock = clockByEmployee[a.id];
      const bClock = clockByEmployee[b.id];
      const aTier = aEntry ? 2 : aClock ? 1 : 0;
      const bTier = bEntry ? 2 : bClock ? 1 : 0;
      if (aTier !== bTier) return aTier - bTier;
      return (a.last_name || '').localeCompare(b.last_name || '');
    });
  }, [filteredRoster, attendanceByEmployee, clockByEmployee]);

  return {
    drivers: enrichedDrivers,
    terminals,
    attendance,
    clockIns,
    attendanceByEmployee,
    clockByEmployee,
    enteredIds,
    roster: sortedRoster,
    loading,
    error,
    search,
    statusFilter,
    setSearch,
    setStatusFilter,
    refreshRoster: load,
  };
}
